import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Text, ActivityIndicator, Platform, useWindowDimensions, Alert } from 'react-native';
import { useDashboardData, DashboardFilters } from '@/hooks/useDashboardData';
import KPIResumeCard from '@/components/admin/KPIResumeCard';
import theme from '@/config/theme';
import DashboardFilter from '@/components/admin/DashboardFilter';
import ChecklistsListNative from '@/components/admin/ChecklistsList.native';
import ChecklistsTableWeb from '@/components/admin/ChecklistsTable.web';
import { Ionicons } from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { AdminLogger } from '@/utils/logger';
import { observer } from '@legendapp/state/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ChecklistActionsHelper from '@/services/checklist/ChecklistActionsHelper';
import { realtimeTrigger } from '@/state/realtime';
import { useWebAlert } from '@/components/WebAlert';
import { showToast } from '@/utils/toast';
import { useFocusEffect } from 'expo-router';
import { globalState } from '@/state/globalState';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';

function DashboardScreen() {
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);

  const log = AdminLogger.Dashboard;
  const buildDefaultFilters = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
    return {
      startDate: firstDay,
      endDate: lastDay,
      status: null,
      templateId: null,
      page: 0,
    };
  };

  const persistedFilters = globalState.ui.adminDashboard.filters.get();
  const [filters, setFilters] = useState<DashboardFilters>(() => persistedFilters ?? buildDefaultFilters());
  const [openRows, setOpenRows] = useState<string[]>(() => globalState.ui.adminDashboard.openRows.get() ?? []);
  log.info('📊 Renderizando DashboardScreen', { filters, platform: Platform.OS });
  const data = useDashboardData(filters);
  const { width, height } = useWindowDimensions();
  const isPortraitWeb = Platform.OS === 'web' && height >= width;
  const [filterVisible, setFilterVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  /**
   * ===== Seleção múltipla =====
   * Os IDs dos checklists selecionados são armazenados em um Set para busca O(1).
   */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /** Alterna seleção de um item */
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /** Seleciona todos os itens da lista atual */
  const selectAll = (ids: string[]) => {
    setSelectedIds(new Set(ids));
  };

  /** Limpa a seleção */
  const clearSelection = () => setSelectedIds(new Set());

  // Limpa seleção sempre que filtros (exceto page) mudarem
  const didInitFiltersRef = useRef(false);
  useEffect(() => {
    if (!didInitFiltersRef.current) {
      didInitFiltersRef.current = true;
      return;
    }
    clearSelection();
    setOpenRows([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.status, filters.templateId]);

  useEffect(() => {
    globalState.ui.adminDashboard.filters.set(filters);
  }, [filters]);

  useEffect(() => {
    globalState.ui.adminDashboard.openRows.set(openRows);
  }, [openRows]);

  const refreshToken = realtimeTrigger.counter.get();

  // Refresh sempre que a tela ganhar foco (útil quando permanece montada em bottom-tabs)
  useFocusEffect(
    useCallback(() => {
      // Reinicia paginação e força novo carregamento
      setFilters((f) => ({ ...f, page: 0 }));
      realtimeTrigger.counter.set((c) => c + 1);
    }, [])
  );

  const periodLabel = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return '';
    const start = format(new Date(filters.startDate), 'dd/MM/yyyy', { locale: ptBR });
    const end = format(new Date(filters.endDate), 'dd/MM/yyyy', { locale: ptBR });
    return `${start}  —  ${end}`;
  }, [filters.startDate, filters.endDate]);

  const loadMore = () => {
    log.info('➡️ loadMore chamado, página anterior', filters.page);
    setFilters({ ...filters, page: filters.page + 1 });
  };

  const formatAverageTime = (valueMs: number) => {
    const totalMs = Number.isFinite(valueMs) && valueMs > 0 ? valueMs : 0;
    const totalMinutes = Math.round(totalMs / 60000);
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, '0');
      const minutes = (totalMinutes % 60).toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return totalMinutes;
  };

  // Handlers de KPI
  const applyStatusFilter = (status: 'done' | 'in_progress' | null) => {
    setFilters({
      ...filters,
      status,
      page: 0,
    });
  };

  // Determina qual KPI está ativo baseado nos filtros atuais
  const selectedKPI = useMemo<null | 'done' | 'in_progress'>(() => {
    if (filters.status === 'done') return 'done';
    if (filters.status === 'in_progress') return 'in_progress';
    return null;
  }, [filters.status]);

  // Hook de alertas web
  const { showAlert } = useWebAlert();
  const logToast = AdminLogger.Dashboard;

  /**
   * Deleção em massa (F3)
   * 1. Confirmação com o usuário.
   * 2. Chama ChecklistActionsHelper.deleteMany.
   * 3. Limpa seleção e força recarregamento dos dados (incrementa realtimeTrigger).
   */
  const handleBulkDelete = async () => {
    if (isDeleting) return; // Prevê duplo clique
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // ---- Confirmação cross-plataforma ----
    const confirmed: boolean = await new Promise((resolve) => {
      if (Platform.OS !== 'android') {
        showAlert('Deletar', `Deletar ${ids.length} checklists?`, [
          {
            text: 'Cancelar',
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: 'Deletar',
            style: 'destructive',
            onPress: () => resolve(true),
          },
        ]);
      } else {
        Alert.alert('Deletar', `Deletar ${ids.length} checklists?`, [
          {
            text: 'Cancelar',
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: 'Deletar',
            style: 'destructive',
            onPress: () => resolve(true),
          },
        ]);
      }
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await ChecklistActionsHelper.deleteMany(ids, { silent: false });
    } catch (err) {
      log.error('Erro na deleção em massa', err);
      showToast('Erro ao deletar checklists. Tente novamente.', { type: 'error', duration: 2500 });
    } finally {
      setIsDeleting(false);
      setSelectedIds(new Set());
      // Após uma deleção em massa, garantimos que a próxima consulta
      // reinicie da primeira página para evitar cenários onde o usuário
      // estava paginado (page > 0) e a listagem retornaria vazia por
      // causa do deslocamento (offset) – observado no mobile.
      setFilters((prev) => ({ ...prev, page: 0 }));
      // Garantir refresh, mesmo em falha parcial
      realtimeTrigger.counter.set((c) => c + 1);
    }
  };

  const hasSelection = selectedIds.size > 0;

  /** Geração de PDF em massa (F5) */
  const handleBulkPdf = async () => {
    if (isGeneratingPdf || !hasSelection) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setIsGeneratingPdf(true);
    const total = ids.length;
    showToast(`Gerando 0/${total} PDFs…`, { type: 'info', duration: 10000, id: 'pdf-progress' });

    try {
      const results = await ChecklistActionsHelper.generatePdfMany(ids, {
        silent: true,
        // 2025: zipFileName só necessário para web; mobile gera ZIP automaticamente para múltiplos PDFs
        zipFileName: Platform.OS === 'web' ? `checklists_${total}.zip` : undefined,
        onProgress: (done, tot) => {
          showToast(`Gerando ${done}/${tot} PDFs…`, { type: 'info', duration: 10000, id: 'pdf-progress' });
        },
      });

      // Mobile: PDF único já é compartilhado internamente; múltiplos viram ZIP automaticamente
      if (Platform.OS !== 'web' && total > 1) {
        log.info('📦 Múltiplos PDFs - ZIP gerado e compartilhado automaticamente', { total });
      } else if (Platform.OS !== 'web' && total === 1) {
        log.info('📄 PDF único compartilhado internamente pelo generateChecklistPdfNative');
      }

      showToast('PDFs gerados!', { type: 'success', duration: 2500, id: 'pdf-progress' });
    } catch (err) {
      log.error('Erro na geração de PDFs em massa', err);
      showToast('Erro ao gerar PDFs', { type: 'error', duration: 2500, id: 'pdf-progress' });
    } finally {
      setIsGeneratingPdf(false);
      clearSelection();
    }
  };

  if (data.loading.get()) {
    log.info('Dashboard data loading...');
  }

  if (!data.loading.get()) {
    log.info(`Dashboard data carregado: ${data.list.get().length} itens`, data.kpis.peek());
  }

  return (
    !premiumAccess ? (
      <View style={styles.restrictedWrapper}>
        <UpgradePrompt variant="restricted" />
      </View>
    ) : (
    <View style={styles.container}>
      {/* Toolbar de seleção múltipla */}
      {selectedIds.size > 0 && (
        <View style={styles.bulkToolbar}>
          <Text style={styles.bulkCount}>{selectedIds.size} selecionado(s)</Text>
          <View style={styles.bulkActions}>
            {/* Ação: Deleção em massa */}
            <TouchableOpacity
              onPress={handleBulkDelete}
              accessibilityRole="button"
              accessibilityLabel="Deletar selecionados"
              disabled={isDeleting}
              style={[styles.bulkAction, isDeleting && styles.bulkActionDisabled]}
            >
              {isDeleting ? (
                <ActivityIndicator size="small" color={theme.colors.util.error} />
              ) : (
                <MaterialCommunityIcons name="trash-can-outline" size={30} color={theme.colors.util.error} />
              )}
              <Text style={styles.bulkActionLabel}>Excluir</Text>
            </TouchableOpacity>
            {/* Ação: Geração de PDF em massa */}
            <TouchableOpacity
              onPress={handleBulkPdf}
              accessibilityRole="button"
              accessibilityLabel="Gerar PDFs selecionados"
              disabled={!hasSelection || isGeneratingPdf}
              style={[styles.bulkAction, (!hasSelection || isGeneratingPdf) && styles.bulkActionDisabled]}
            >
              {isGeneratingPdf ? (
                <ActivityIndicator size="small" color={theme.colors.secondary.dark} />
              ) : (
                <MaterialCommunityIcons
                  name="file-pdf-box"
                  size={32}
                  color={hasSelection ? theme.colors.secondary.dark : theme.colors.text.tertiary}
                />
              )}
              <Text style={styles.bulkActionLabel}>Gerar PDF</Text>
            </TouchableOpacity>
            {/* Limpar seleção */}
            <TouchableOpacity
              onPress={() => {
                if (isDeleting) return;
                clearSelection();
              }}
              accessibilityRole="button"
              accessibilityLabel="Limpar seleção"
              disabled={isDeleting}
              style={[styles.bulkAction, isDeleting && styles.bulkActionDisabled]}
            >
              <MaterialCommunityIcons name="close" size={28} color={theme.colors.text.primary} />
              <Text style={styles.bulkActionLabel}>Cancelar</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* FAB Selecionar Todos (mobile) */}
      {Platform.OS !== 'web' && selectedIds.size === 0 && data.list.get().length > 0 && (
        <TouchableOpacity
          style={styles.fabSelectAll}
          onPress={() => selectAll(data.list.get().map((i) => i.id))}
          accessibilityRole="button"
          accessibilityLabel={`Selecionar todos (${data.list.get().length})`}
        >
          <MaterialCommunityIcons name="checkbox-multiple-marked" size={28} color={theme.colors.text.inverse} />
        </TouchableOpacity>
      )}      

      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Dashboard</Text>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Filtrar checklists" onPress={() => setFilterVisible(true)}>
          <Ionicons name="filter" size={24} color={theme.colors.primary.default} />
        </TouchableOpacity>
      </View>

      {/* Período Selecionado */}
      {periodLabel ? (
        <Text style={styles.periodText}>Período: {periodLabel}</Text>
      ) : null}

      {/* KPI Cards */}
      {Platform.OS === 'web' && !isPortraitWeb ? (
        <View style={styles.kpiGridWeb}>
          <KPIResumeCard
            title="Concluídos"
            value={data.kpis.done.get()}
            colors={[theme.colors.util.success, theme.colors.util.success]}
            onPress={() => applyStatusFilter(selectedKPI === 'done' ? null : 'done')}
            selected={selectedKPI === 'done'}
          />
          <KPIResumeCard
            title="Em Progresso"
            value={data.kpis.inProgress.get()}
            colors={[...theme.colors.secondary.gradient] as [string, string]}
            onPress={() => applyStatusFilter(selectedKPI === 'in_progress' ? null : 'in_progress')}
            selected={selectedKPI === 'in_progress'}
          />
          <KPIResumeCard
            title={data.kpis.averageTime.get() > 3600000 ? 'Tempo Médio (h)' : 'Tempo Médio (min)'}
            value={formatAverageTime(data.kpis.averageTime.get())}
          />
        </View>
      ) : (Platform.OS === 'web' && isPortraitWeb) || Platform.OS !== 'web' ? (
        <View style={styles.kpiRow}>
          <KPIResumeCard
            title="Concluídos"
            value={data.kpis.done.get()}
            colors={[theme.colors.util.success, theme.colors.util.success]}
            onPress={() => applyStatusFilter(selectedKPI === 'done' ? null : 'done')}
            selected={selectedKPI === 'done'}
          />
          <KPIResumeCard
            title="Em Progresso"
            value={data.kpis.inProgress.get()}
            colors={[...theme.colors.secondary.gradient] as [string, string]}
            onPress={() => applyStatusFilter(selectedKPI === 'in_progress' ? null : 'in_progress')}
            selected={selectedKPI === 'in_progress'}
          />
          <KPIResumeCard
            title={data.kpis.averageTime.get() > 3600000 ? 'Tempo Médio (h)' : 'Tempo Médio (min)'}
            value={formatAverageTime(data.kpis.averageTime.get())}
          />
        </View>
      ) : null}

      {/* List */}
      <View style={[styles.listContainer]}>
        {Platform.OS === 'web' && !isPortraitWeb ? (
          <ChecklistsTableWeb
            data={data.list.get()}
            loading={data.loading.get()}
            totalCount={data.totalCount.get()}
            hasMore={data.hasMore.get()}
            onLoadMore={loadMore}
            selectedIds={selectedIds}
            toggleSelect={toggleSelect}
            selectAll={selectAll}
            openRows={openRows}
            onOpenRowsChange={setOpenRows}
            refreshToken={refreshToken}
          />
        ) : (
          <ChecklistsListNative
            data={data.list.get()}
            loading={data.loading.get()}
            onEndReached={loadMore}
            selectedIds={selectedIds}
            toggleSelect={toggleSelect}
            hasMore={data.hasMore.get()}
            openRows={openRows}
            onOpenRowsChange={setOpenRows}
            refreshToken={refreshToken}
          />
        )}
        {data.loading.get() && Platform.OS !== 'web' && (
          <ActivityIndicator style={{ marginTop: 16 }} color={theme.colors.primary.default} />
        )}
      </View>

      {/* Filter Modal */}
      <DashboardFilter visible={filterVisible} onClose={() => setFilterVisible(false)} filters={filters} setFilters={(f) => setFilters({ ...f, page: 0 })} />
    </View>
    )
  );
}

const styles = StyleSheet.create({
  restrictedWrapper: {
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background.secondary,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: Platform.OS === 'web' ? 16 : 48,
    paddingBottom: 16,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.background.primary,
    ...theme.shadows.sm,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    flex: 1,
  },
  kpiGridWeb: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    paddingTop: theme.spacing.sm,
  },
  listContainer: {
    flex: 1,
    ...(Platform.OS === 'web' ? { 
      paddingHorizontal: theme.spacing.screen.horizontal,
      paddingTop: theme.spacing.sm,
    } : {}),
  },
  periodText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    paddingHorizontal: 16,
    paddingTop: 4,
  },
  kpiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  bulkToolbar: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulkCount: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
    width: '100%',
  },
  bulkActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: theme.spacing.xs,
  },
  bulkAction: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
    marginBottom: theme.spacing.xs,
    minWidth: 80,
  },
  bulkActionDisabled: {
    opacity: 0.5,
  },
  bulkActionLabel: {
    marginTop: theme.spacing.xs / 2,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
    color: theme.colors.text.primary,
    textAlign: 'center',
  },
  fabSelectAll: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primary.default,
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.lg,
    zIndex: theme.zIndex.card,
  },
});

export default observer(DashboardScreen);
